<script setup>
// The template rendered as the WhatsApp conversation the contact will actually see:
// chat header, wallpaper, incoming bubble with the artwork inside it, timestamp and
// reply buttons.
//
// Artwork and copy are one thing here, not two panes. On the phone the image *is* the
// top of the message — judging the creative apart from the text it sits above is how you
// approve a campaign whose picture and words disagree.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  template: { type: Object, default: null },
  // The preview is framed as the chat the contact will actually see, so it carries the
  // agent's identity — the same avatar and name that show up on WhatsApp.
  agentName: { type: String, default: '' },
  agentAvatar: { type: String, default: '' },
});

const { t } = useI18n();

const agentInitials = computed(() =>
  (props.agentName || '?')
    .split(/\s+/)
    .slice(0, 2)
    .map(word => word.charAt(0).toUpperCase())
    .join('')
);

function componentOfType(type) {
  return (props.template?.components || []).find(c => c.type === type) || null;
}

const header = computed(() => componentOfType('HEADER'));
const bodyText = computed(() => componentOfType('BODY')?.text || '');
const footerText = computed(() => componentOfType('FOOTER')?.text || '');
const buttons = computed(() => componentOfType('BUTTONS')?.buttons || []);

const headerMediaUrl = computed(
  () =>
    props.template?.preview_image_url ||
    header.value?.example?.header_handle?.[0] ||
    ''
);
const isImageHeader = computed(
  () => header.value?.format === 'IMAGE' && headerMediaUrl.value
);

// WhatsApp renders each button type with its own affordance; a flat list of grey pills
// reads as "some buttons" and hides that one of them leaves the app.
function buttonIcon(button) {
  switch (button.type) {
    case 'URL':
      return 'i-lucide-external-link';
    case 'PHONE_NUMBER':
      return 'i-lucide-phone';
    case 'COPY_CODE':
      return 'i-lucide-copy';
    default:
      return 'i-lucide-reply';
  }
}

// Escape first, then apply WhatsApp markup. Template text is Meta-approved, but it still
// arrives over the network — never interpolate it raw.
function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const formattedBody = computed(() =>
  escapeHtml(bodyText.value)
    .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
    .replace(/_([^_\n]+)_/g, '<em>$1</em>')
    .replace(/~([^~\n]+)~/g, '<s>$1</s>')
    // {{Nome}} / {{1}} placeholders — highlight so unfilled variables are obvious.
    .replace(
      /\{\{([^}]+)\}\}/g,
      '<span class="px-1 rounded bg-n-alpha-1 text-n-brand">{{$1}}</span>'
    )
    .replace(/\n/g, '<br />')
);
</script>

<template>
  <div v-if="template" class="w-full">
    <div
      class="overflow-hidden border shadow-sm rounded-2xl border-n-weak bg-n-background"
    >
      <!-- Chat header -->
      <div class="flex items-center gap-3 px-3 py-2 bg-n-teal-9">
        <i class="text-white/90 i-lucide-arrow-left size-4 shrink-0" />
        <img
          v-if="agentAvatar"
          :src="agentAvatar"
          alt=""
          class="object-cover rounded-full size-9 shrink-0"
        />
        <span
          v-else
          class="flex items-center justify-center text-xs font-semibold text-white rounded-full size-9 shrink-0 bg-n-teal-10"
        >
          {{ agentInitials }}
        </span>
        <div class="min-w-0">
          <p class="text-sm font-medium text-white truncate">
            {{ agentName || t('OUTBOUNDS.TEMPLATE.PREVIEW_AGENT') }}
          </p>
          <p class="text-[10px] text-white/75">
            {{ t('OUTBOUNDS.TEMPLATE.PREVIEW_ONLINE') }}
          </p>
        </div>
        <i class="ml-auto text-white/90 i-lucide-video size-4 shrink-0" />
        <i class="text-white/90 i-lucide-phone size-4 shrink-0" />
      </div>

      <!-- Chat area. The whole conversation scrolls, so a tall creative does not stretch
           the page — the operator scrolls the phone, like a phone. -->
      <div
        class="overflow-y-auto px-3 py-4 max-h-[32rem] bg-n-alpha-2 bg-[radial-gradient(rgba(120,110,90,0.10)_1px,transparent_1px)] bg-[length:18px_18px]"
      >
        <div class="max-w-[19rem]">
          <div class="relative">
            <!-- Bubble tail: the detail that makes the mock read as WhatsApp rather than
                 as a card with rounded corners. -->
            <span
              class="absolute top-0 -left-1.5 size-3 bg-n-background [clip-path:polygon(100%_0,0_0,100%_100%)]"
            />

            <div
              class="relative overflow-hidden shadow-sm rounded-lg rounded-tl-none bg-n-background"
            >
              <img
                v-if="isImageHeader"
                :src="headerMediaUrl"
                alt=""
                class="w-full object-contain max-h-[22rem] bg-n-alpha-2"
              />
              <div
                v-else-if="header?.format && header.format !== 'TEXT'"
                class="flex items-center gap-2 px-3 py-5 text-xs bg-n-alpha-2 text-n-slate-10"
              >
                <i class="i-lucide-paperclip size-4" />
                {{ header.format }}
              </div>

              <div class="px-2.5 py-2">
                <p
                  v-if="header?.format === 'TEXT' && header?.text"
                  class="mb-1 text-sm font-semibold text-n-slate-12"
                >
                  {{ header.text }}
                </p>

                <!-- eslint-disable-next-line vue/no-v-html -->
                <p
                  class="text-sm leading-relaxed whitespace-pre-line text-n-slate-12"
                  v-html="formattedBody"
                />

                <p v-if="footerText" class="mt-1.5 text-[11px] text-n-slate-9">
                  {{ footerText }}
                </p>

                <p class="mt-0.5 text-[10px] text-right text-n-slate-9">
                  {{ t('OUTBOUNDS.TEMPLATE.PREVIEW_TIME') }}
                </p>
              </div>

              <!-- Buttons live inside the bubble, divided, the way Meta renders them. -->
              <div v-if="buttons.length" class="border-t border-n-weak">
                <div
                  v-for="(button, index) in buttons"
                  :key="index"
                  class="flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-n-blue-text"
                  :class="index ? 'border-t border-n-weak' : ''"
                >
                  <i :class="`${buttonIcon(button)} size-3.5`" />
                  {{ button.text }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <p v-else class="text-sm text-n-slate-10">
    {{ t('OUTBOUNDS.TEMPLATE.NO_SELECTION') }}
  </p>
</template>
